// What's on screen right now, in the page's own words, for Bailey. A page
// publishes a few plain lines while it's open ("2 drafts and 1 planned
// post", "Tomorrow has 12 free times"); the context Bailey gets with a
// message includes the ones for the page the person is on. Nothing here is
// sent anywhere by itself.
import { useEffect } from 'react'

const pages = new Map() // key -> { path, lines, entity, at }
// A page's last facts after it closes, for the Bailey tab ("is it busy?" asked
// right after leaving the diary). Kept briefly; the pet only uses live ones.
const left = new Map() // key -> { path, lines, entity, at, leftAt }
const RECENT_MS = 15 * 60 * 1000
const listeners = new Set()
const notify = () => listeners.forEach((fn) => { try { fn() } catch (e) { /* ignore */ } })

const tidy = (lines) => (Array.isArray(lines) ? lines : [lines])
  .map(l => String(l == null ? '' : l).replace(/\s+/g, ' ').trim())
  .filter(Boolean)
  .slice(0, 8)
  .map(l => (l.length > 200 ? `${l.slice(0, 199)}…` : l))

// The one thing open on the page, so Bailey can act on "this client":
// { type: 'client' | 'booking', id, label }
const tidyEntity = (e) => (e && (e.type === 'client' || e.type === 'booking') && /^[0-9a-f]{24}$/i.test(String(e.id || ''))
  ? { type: e.type, id: String(e.id), label: String(e.label || '').replace(/\s+/g, ' ').trim().slice(0, 120) }
  : null)

// `path` is the start of the address the facts belong to ("/marketing-social")
export const publishFacts = (key, { path, lines, entity }) => {
  if (!key) return
  pages.set(key, { path: path || '', lines: tidy(lines || []), entity: tidyEntity(entity), at: Date.now() })
  left.delete(key)
  notify()
}
export const clearFacts = (key) => {
  const was = pages.get(key)
  if (!was) return
  pages.delete(key)
  left.set(key, { ...was, leftAt: Date.now() })
  notify()
}

const matches = (here) => (p) => !p.path || here === p.path || here.indexOf(`${p.path}/`) === 0 || here.indexOf(p.path) === 0
// Live facts, plus (for the Bailey tab) those of pages closed in the last few minutes
const sources = (here, recent) => {
  const now = Date.now()
  const live = Array.from(pages.values())
  const gone = recent ? Array.from(left.values()).filter(p => now - p.leftAt < RECENT_MS) : []
  return live.concat(gone).filter(matches(here)).sort((a, b) => b.at - a.at)
}

// The facts for where the person is now, newest first
export const factsFor = (pathname, { recent = false } = {}) => {
  const here = String(pathname || '')
  return sources(here, recent)
    .filter(p => p.lines.length)
    .reduce((all, p) => all.concat(p.lines), [])
    .slice(0, 10)
}

// The one thing open where they are, if a page said so
export const entityFor = (pathname, { recent = false } = {}) => {
  const hit = sources(String(pathname || ''), recent).find(p => p.entity)
  return hit ? hit.entity : null
}

export const onFacts = (fn) => { listeners.add(fn); return () => listeners.delete(fn) }

// A page's facts while it's on screen: published when they change, cleared
// when it goes. `lines` should be plain, short and true.
export const usePageFacts = (key, path, lines, entity) => {
  const said = tidy(lines || []).join('\n')
  const ent = tidyEntity(entity)
  const entKey = ent ? `${ent.type}:${ent.id}:${ent.label}` : ''
  useEffect(() => {
    if (said || ent) publishFacts(key, { path, lines: said ? said.split('\n') : [], entity: ent })
    else clearFacts(key)
  }, [key, path, said, entKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearFacts(key), [key])
}
